export interface IcContext {
  icLabel: string | null;
  icDomain: string | null;
  isolationContextId: string | null;
  perimeter: string | null;
}

// GlobalEdge IC cookie header names as per documentation
// https://dogfooding.atlassian-us-gov-mod.net/wiki/spaces/IP1/pages/130187649/LDR+Information+about+Isolation+Context+will+propagate+to+fronted+through+Cookie
const PERIMETER_COOKIE = 'Atl-Ctx-Perimeter';
const ISOLATION_CONTEXT_ID_COOKIE = 'Atl-Ctx-Isolation-Context-Id';
const ISOLATION_CONTEXT_DOMAIN_COOKIE = 'Atl-Ctx-Isolation-Context-Domain';

/**
 * Helper function to get a specific cookie value
 */
const getCookieValue = (cookieName: string): string | null => {
  if (typeof document === 'undefined') {
    return null;
  }

  const cookies = document.cookie.split(';');
  const cookie = cookies.find(cookie => cookie.trim().startsWith(`${cookieName}=`));

  if (!cookie) {
    return null;
  }

  try {
    const cookieValue = cookie.split('=', 2)[1];
    return decodeURIComponent(cookieValue);
  } catch (error) {
    console.warn(`Failed to parse ${cookieName} cookie:`, error);
    return null;
  }
};

const parseIcLabel = (): string | null => {
  const cookieValue = getCookieValue(ISOLATION_CONTEXT_DOMAIN_COOKIE);
  if (!cookieValue || cookieValue.trim() === '') {
    return null;
  }
  const splitDomain = cookieValue.trim().split('.');

  return splitDomain.length > 0 ? splitDomain[0] : null;
};

/**
 * Parse IC context information from the GlobalEdge cookies
 *
 * https://dogfooding.atlassian-us-gov-mod.net/wiki/spaces/IP1/pages/130187649/LDR+Information+about+Isolation+Context+will+propagate+to+fronted+through+Cookie
 * https://hello.atlassian.net/wiki/spaces/EDGE/pages/4722701133/Domain+nomenclature
 *
 * GlobalEdge provides:
 * - Atl-Ctx-Perimeter: perimeter info (fedramp-moderate | commercial)
 * - Atl-Ctx-Isolation-Context-Id: IC identifier (e.g., ic-001)
 * - Atl-Ctx-Isolation-Context-Domain: IC domain name (e.g., simcity.atlassian-isolated.net)
 *    (We manually parse this base domain to produce the ic-label)
 */
export const parseIcCookie = (): IcContext => {
  const isolationContextDomain = getCookieValue(ISOLATION_CONTEXT_DOMAIN_COOKIE);
  const isolationContextId = getCookieValue(ISOLATION_CONTEXT_ID_COOKIE);
  const perimeter = getCookieValue(PERIMETER_COOKIE);

  const icLabel = parseIcLabel();

  return {
    icLabel: icLabel,
    icDomain: isolationContextDomain,
    isolationContextId: isolationContextId,
    perimeter: perimeter,
  };
};

export const getIcLabel = (): string | null => {
  // icLabel maps to the parsed IC domain identifier (pear, apple, etc.)
  return parseIcLabel();
};

export const getIcDomain = (): string | null => {
  // icDomain returns the raw IC base domain (pear.oasis-stg.com, apple.atlassian-isolated.net)
  return getCookieValue(ISOLATION_CONTEXT_DOMAIN_COOKIE);
};

export const getICId = (): string | null => {
  // The unique IC ID
  return getCookieValue(ISOLATION_CONTEXT_ID_COOKIE);
};

export const getPerimeter = (): string | null => {
  // Get perimeter information (fedramp-moderate | commercial)
  return getCookieValue(PERIMETER_COOKIE);
};
